/**
 * The tiled part of the layout: splits (with draggable dividers) and tab groups. Each view
 * finds its area by id in the current layout, so a layout change only re-renders what moved.
 */
import { For, Show, createMemo, onMount, type JSX } from "solid-js";
import { useApp } from "../app/context";
import * as L from "./layout";
import type { AreaContext } from "./areaContext";
import { clamp } from "./geometry";
import { WindowBody } from "./WindowBody";

/** A split or a tab group, found by id in the current layout. */
export function AreaView(props: { id: string; ctx: AreaContext }): JSX.Element {
  const { wm } = useApp();
  const area = createMemo(() => L.findArea(wm.layout().root, props.id));
  const kind = createMemo(() => area()?.kind);
  return (
    <Show when={kind()} keyed>
      {(k) => (k === "split" ? <SplitView id={props.id} ctx={props.ctx} /> : <TabsView id={props.id} ctx={props.ctx} />)}
    </Show>
  );
}

/** Children side by side or stacked, with draggable dividers between them. */
export function SplitView(props: { id: string; ctx: AreaContext }): JSX.Element {
  const { wm } = useApp();
  let el!: HTMLDivElement;
  const area = createMemo(() => {
    const a = L.findArea(wm.layout().root, props.id);
    return a?.kind === "split" ? a : null;
  });
  const childIds = createMemo(() => area()?.children.map((c) => c.id) ?? []);
  /** `true` when the children sit side by side. */
  const row = () => area()?.direction === "row";

  /** Drags the divider after child `index`, keeping both sides above the minimum size. */
  function resizeFrom(event: PointerEvent, index: number): void {
    event.preventDefault();
    const a = area();
    if (!a) return;
    const rect = el.getBoundingClientRect();
    const total = row() ? rect.width : rect.height;
    const start = row() ? event.clientX : event.clientY;
    const sizes = [...a.sizes];
    const pair = sizes[index]! + sizes[index + 1]!;
    const move = (ev: PointerEvent) => {
      const delta = ((row() ? ev.clientX : ev.clientY) - start) / total;
      const left = clamp(sizes[index]! + delta, L.MIN_SIZE, pair - L.MIN_SIZE);
      const next = [...sizes];
      next[index] = left;
      next[index + 1] = pair - left;
      wm.update((l) => L.resize(l, props.id, next));
    };
    const up = () => {
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", up);
    };
    addEventListener("pointermove", move);
    addEventListener("pointerup", up);
  }

  return (
    <div class="split" classList={{ row: row(), column: !row() }} ref={el}>
      <For each={childIds()}>
        {(id, i) => (
          <>
            <Show when={i() > 0}>
              <div class="divider" role="separator" onPointerDown={(e) => resizeFrom(e, i() - 1)} />
            </Show>
            <div class="split-cell" style={{ flex: `${area()?.sizes[i()] ?? 1} 1 0px` }}>
              <AreaView id={id} ctx={props.ctx} />
            </div>
          </>
        )}
      </For>
    </div>
  );
}

/** A group of windows shown as tabs. */
export function TabsView(props: { id: string; ctx: AreaContext }): JSX.Element {
  const { wm } = useApp();
  let el!: HTMLDivElement;
  const area = createMemo(() => {
    const a = L.findArea(wm.layout().root, props.id);
    return a?.kind === "tabs" ? a : null;
  });
  const windows = createMemo(() => area()?.windows ?? []);
  const active = createMemo(() => area()?.active ?? null);
  onMount(() => props.ctx.register(props.id, el));

  /** Detaches the active tab into a floating window. */
  const floatActive = () => {
    const win = active();
    if (!win) return;
    const r = el.getBoundingClientRect();
    const ws = el.closest(".workspace")!.getBoundingClientRect();
    wm.float(win, { x: r.left - ws.left + 24, y: r.top - ws.top + 24, width: Math.min(520, r.width), height: Math.min(420, r.height) });
  };

  return (
    <section class="win tabs-area" ref={el} data-area={props.id}>
      <header class="tabbar">
        <For each={windows()}>
          {(win) => (
            <div class="tab" classList={{ active: win === active() }} data-tab={win} onPointerDown={(e) => props.ctx.beginDrag(e, win, "tab")}>
              <button type="button" class="tab-label" onClick={() => wm.update((l) => L.activate(l, win))}>
                {wm.def(win)?.title ?? win}
              </button>
              <button type="button" class="tab-x" title="Close window" aria-label={`Close ${wm.def(win)?.title ?? win}`} onPointerDown={(e) => e.stopPropagation()} onClick={() => wm.close(win)}>
                ×
              </button>
            </div>
          )}
        </For>
        <span class="sp" />
        <button type="button" class="win-btn" title="Float this window" aria-label="Float window" data-float onClick={floatActive}>
          ⧉
        </button>
      </header>
      <Show when={active()} keyed>
        {(win) => <WindowBody window={win} />}
      </Show>
    </section>
  );
}
